import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { showErrorToast, showSuccessToast } from '@ui';
import { translate } from '@utils/i18n';
import { bulkUpdateAttachmentReportOptions, bulkUpdateAttachmentResult } from '../services/inspectionAttachmentsApi';
import { MASS_ACTIONS_NS } from '../constants';
/**
 * Inspection's own bulk edits — result and the two report-option booleans. Delete is not here: it
 * is the one mass action every attachment surface has, so it lives in the shared
 * `useAttachmentDeletion`.
 *
 * Selection semantics: always the explicit checked-row `ids`. Unlike Building's endpoint, this
 * one has NO "absent ids falls back to the current filtered set" mode — absent and empty both
 * resolve to no rows server-side, so a cleared selection can never mass-edit the whole inspection.
 */
export function useAttachmentBulkEdits({ inspectionId, selectedIds, clearSelection, invalidate }) {
    const afterSuccess = useCallback((messageKey) => {
        showSuccessToast(translate(messageKey, { namespace: MASS_ACTIONS_NS }));
        clearSelection();
        invalidate();
    }, [clearSelection, invalidate]);
    // On failure: toast AND still invalidate, so the UI re-syncs with the server rather than showing
    // a stale state. Selection is left as-is since nothing here changed it optimistically.
    const afterError = useCallback((messageKey) => {
        showErrorToast(translate(messageKey, { namespace: MASS_ACTIONS_NS }));
        invalidate();
    }, [invalidate]);
    const resultMutation = useMutation({
        mutationFn: (answer) => bulkUpdateAttachmentResult(inspectionId, selectedIds, answer),
        onSuccess: () => afterSuccess('toasts.result_success'),
        onError: () => afterError('toasts.result_error')
    });
    const reportOptionsMutation = useMutation({
        mutationFn: (options) => bulkUpdateAttachmentReportOptions(inspectionId, selectedIds, options),
        onSuccess: () => afterSuccess('toasts.report_success'),
        onError: () => afterError('toasts.report_error')
    });
    return {
        isMutating: resultMutation.isLoading || reportOptionsMutation.isLoading,
        updateResult: useCallback((answer) => resultMutation.mutate(answer), [resultMutation]),
        updateReportOptions: useCallback((options) => reportOptionsMutation.mutate(options), [reportOptionsMutation])
    };
}
